import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Alert from '@/components/Alert';
import { Logo } from '@/components/Logo';
import { postAuth } from '@/components/TwoStepForm';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { useQuery } from 'hooks/query';

import { ChainlitContext } from 'client-types/*';

// The activation link from the invite email (two_step_cli.py invite). The
// first setup of the second step happens here, never on the login screen:
// whoever opens this link proved they read that mailbox.

interface Info {
  email: string;
  send_to_hint: string;
  needs_password: boolean;
  totp_ready: boolean;
  trust_days: number;
}

interface TotpSetup {
  setup_key: string;
  uri: string;
  qr_svg: string | null;
}

type Step =
  | 'loading'
  | 'dead'
  | 'password'
  | 'choose'
  | 'totp'
  | 'backup'
  | 'done';

export default function Activate() {
  const query = useQuery();
  const token = query.get('t') || '';
  const apiClient = useContext(ChainlitContext);
  const navigate = useNavigate();
  const url = (p: string) => apiClient.buildEndpoint(p);

  const [step, setStep] = useState<Step>('loading');
  const [info, setInfo] = useState<Info | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [totp, setTotp] = useState<TotpSetup | null>(null);
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    postAuth(url('/auth/activate/info'), { token })
      .then((json: Info) => {
        setInfo(json);
        setStep(json.needs_password ? 'password' : 'choose');
      })
      .catch((err) => {
        setError(err.message);
        setStep('dead');
      });
  }, [token]);

  const savePassword = (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== password2) {
      setError('The two passwords are not the same.');
      return;
    }
    run(async () => {
      await postAuth(url('/auth/activate/password'), { token, password });
      setStep('choose');
    });
  };

  const startTotp = () =>
    run(async () => {
      setTotp(await postAuth(url('/auth/activate/totp/start'), { token }));
      setCode('');
      setStep('totp');
    });

  const confirmTotp = (e: React.FormEvent) => {
    e.preventDefault();
    run(async () => {
      const json = await postAuth(url('/auth/activate/totp/confirm'), {
        token,
        code
      });
      setBackupCodes(json.backup_codes || []);
      setStep('backup');
    });
  };

  const finish = () =>
    run(async () => {
      await postAuth(url('/auth/activate/finish'), { token, remember: true });
      setStep('done');
    });

  const qrSrc = totp?.qr_svg
    ? `data:image/svg+xml;utf8,${encodeURIComponent(totp.qr_svg)}`
    : null;

  return (
    <div className="flex min-h-svh flex-col gap-4 p-6 md:p-10">
      <div className="flex justify-center gap-2 md:justify-start">
        <Logo className="w-[150px]" />
      </div>
      <div className="flex flex-1 items-center justify-center">
        <div className="flex w-full max-w-sm flex-col gap-6">
          {step === 'loading' ? (
            <p className="text-center text-muted-foreground">Loading…</p>
          ) : null}

          {step === 'dead' ? (
            <>
              <h1 className="text-center text-2xl font-bold">
                This link does not work
              </h1>
              <Alert variant="error">{error}</Alert>
            </>
          ) : null}

          {info && step !== 'dead' && step !== 'loading' ? (
            <p className="text-center text-sm text-muted-foreground">
              Account: <b>{info.email}</b>
            </p>
          ) : null}

          {error && step !== 'dead' ? (
            <Alert variant="error">{error}</Alert>
          ) : null}

          {step === 'password' ? (
            <form onSubmit={savePassword} className="flex flex-col gap-4">
              <h1 className="text-center text-2xl font-bold">
                Choose your password
              </h1>
              <div className="grid gap-2">
                <Label htmlFor="pw1">Password</Label>
                <Input
                  id="pw1"
                  type="password"
                  autoComplete="new-password"
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  At least 8 characters.
                </p>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="pw2">Type it again</Label>
                <Input
                  id="pw2"
                  type="password"
                  autoComplete="new-password"
                  value={password2}
                  onChange={(e) => setPassword2(e.target.value)}
                />
              </div>
              <Button type="submit" disabled={busy || !password}>
                Continue
              </Button>
            </form>
          ) : null}

          {step === 'choose' && info ? (
            <div className="flex flex-col gap-4">
              <h1 className="text-center text-2xl font-bold">
                Protect your account
              </h1>
              <p className="text-sm text-muted-foreground">
                When you sign in from a new device, we will ask for a code. Your
                usual devices are remembered for {info.trust_days} days.
              </p>
              <Button disabled={busy} onClick={startTotp}>
                Set up an authenticator app (recommended)
              </Button>
              <p className="text-xs text-muted-foreground">
                Google Authenticator or Microsoft Authenticator on your phone.
                It works without internet and takes about 2 minutes.
              </p>
              <Button variant="outline" disabled={busy} onClick={finish}>
                Send codes to my email only
              </Button>
              <p className="text-xs text-muted-foreground">
                Codes will go to {info.send_to_hint}, the address this link was
                sent to.
              </p>
            </div>
          ) : null}

          {step === 'totp' && totp ? (
            <form onSubmit={confirmTotp} className="flex flex-col gap-4">
              <h1 className="text-center text-2xl font-bold">
                Link your phone
              </h1>
              <ol className="list-decimal space-y-1 pl-5 text-sm">
                <li>
                  Install Google Authenticator or Microsoft Authenticator from
                  your phone's app store.
                </li>
                <li>Open it, tap +, and scan this square.</li>
                <li>Type the 6 digits the app shows.</li>
              </ol>
              {qrSrc ? (
                <img
                  src={qrSrc}
                  alt="QR code for the authenticator app"
                  className="mx-auto h-48 w-48 rounded bg-white p-2"
                />
              ) : null}
              <p className="text-center text-xs text-muted-foreground">
                Can't scan? Enter this key in the app instead:
                <br />
                <code className="select-all break-all">{totp.setup_key}</code>
              </p>
              <div className="grid gap-2">
                <Label htmlFor="totp-code">6-digit code</Label>
                <Input
                  id="totp-code"
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="text-center tracking-[0.3em]"
                />
              </div>
              <Button type="submit" disabled={busy || !code.trim()}>
                Confirm
              </Button>
              <Button
                type="button"
                variant="link"
                onClick={() => setStep('choose')}
              >
                Back
              </Button>
            </form>
          ) : null}

          {step === 'backup' ? (
            <div className="flex flex-col gap-4">
              <h1 className="text-center text-2xl font-bold">
                Save your backup codes
              </h1>
              <p className="text-sm text-muted-foreground">
                If you lose your phone, each of these codes lets you sign in
                once. Print them or write them down and keep them away from your
                computer. They will not be shown again.
              </p>
              <div className="grid grid-cols-2 gap-2 rounded border p-3 font-mono text-sm">
                {backupCodes.map((c) => (
                  <span key={c}>{c}</span>
                ))}
              </div>
              <Button disabled={busy} onClick={finish}>
                I saved them, continue
              </Button>
            </div>
          ) : null}

          {step === 'done' ? (
            <div className="flex flex-col gap-4 text-center">
              <h1 className="text-2xl font-bold">Your account is ready</h1>
              <p className="text-sm text-muted-foreground">
                This device is remembered for {info?.trust_days ?? 30} days.
                Sign in with your email and the password you chose.
              </p>
              <Button onClick={() => navigate('/login')}>Sign in</Button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
